import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  Box,
  Typography,
  Button,
  Stack,
  Badge
} from '@mui/material';
import {
  Share as ShareIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { RecipientPicker } from '../ui/RecipientPicker/RecipientPicker';
import { RecipientSelection } from '../ui/RecipientPicker/types';

export interface SharingFilterPopoverProps {
  selectedGroupId: string;
  selectedUserId: string;
  onGroupChange: (groupId: string) => void;
  onUserChange: (userId: string) => void;
}

/**
 * SharingFilterPopover - Collapsible sharing filter component
 *
 * Provides a button that opens a popover with the modern RecipientPicker for:
 * - Group selection (filter by documents shared with a specific group)
 * - User selection (filter by documents shared with a specific user)
 *
 * Features:
 * - Icons and avatars (👥 groups, 👤 users)
 * - No user limits (uses useUsersForSharing with pagination)
 * - Recently used items with Zustand persistence
 * - Full accessibility (WCAG 2.1 Level AA)
 * - Keyboard navigation
 *
 * Shows a badge indicator when any sharing filter is active.
 */
export const SharingFilterPopover: React.FC<SharingFilterPopoverProps> = ({
  selectedGroupId,
  selectedUserId,
  onGroupChange,
  onUserChange
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  // Convert props to RecipientSelection format for local state
  const [localSelection, setLocalSelection] = React.useState<RecipientSelection>({
    users: selectedUserId ? [selectedUserId] : [],
    groups: selectedGroupId ? [selectedGroupId] : []
  });

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Check if any sharing filter is active
  const hasActiveSharingFilters = Boolean(selectedGroupId || selectedUserId);

  // Count active filters for badge
  const activeFilterCount = [selectedGroupId, selectedUserId].filter(Boolean).length;

  const handleClearFilters = () => {
    const emptySelection: RecipientSelection = { users: [], groups: [] };
    setLocalSelection(emptySelection);
    onGroupChange('');
    onUserChange('');
  };

  const handleApplyFilters = () => {
    // Convert RecipientSelection back to individual IDs
    // Note: Filter popover only supports single selection, so take first item
    const groupId = localSelection.groups[0] || '';
    const userId = localSelection.users[0] || '';

    onGroupChange(groupId);
    onUserChange(userId);
    handleClosePopover();
  };

  const handleSelectionChange = (selection: RecipientSelection) => {
    // For filters, only allow single selection for each type
    // Keep only the most recently selected item
    setLocalSelection({
      users: selection.users.slice(-1), // Keep only the last selected user
      groups: selection.groups.slice(-1) // Keep only the last selected group
    });
  };

  // Sync local state when props change (e.g., external clear)
  React.useEffect(() => {
    setLocalSelection({
      users: selectedUserId ? [selectedUserId] : [],
      groups: selectedGroupId ? [selectedGroupId] : []
    });
  }, [selectedGroupId, selectedUserId]);

  return (
    <>
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        invisible={!hasActiveSharingFilters}
      >
        <Button
          variant={hasActiveSharingFilters ? 'contained' : 'outlined'}
          size="small"
          startIcon={<ShareIcon />}
          onClick={handleOpenPopover}
          aria-label={t('documents.filters.sharingFilters')}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {t('documents.filters.sharingFilters')}
        </Button>
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        role="dialog"
        aria-label={t('documents.filters.sharingFilters')}
      >
        <Box sx={{ p: 3, minWidth: 500 }}>
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {t('documents.filters.sharingFilters')}
              </Typography>
              {hasActiveSharingFilters && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  aria-label={t('documents.filters.clearFilters')}
                >
                  {t('documents.filters.clearFilters')}
                </Button>
              )}
            </Box>

            {/* RecipientPicker with side-by-side variant */}
            <RecipientPicker
              value={localSelection}
              onChange={handleSelectionChange}
              variant="side-by-side"
              showUsers={true}
              showGroups={true}
              showRecent={true}
            />

            {/* Helper text */}
            <Typography variant="caption" color="text.secondary">
              {t('documents.filters.sharingFilterHint')}
            </Typography>

            {/* Apply Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleApplyFilters}
              aria-label={t('documents.filters.applyFilters')}
            >
              {t('documents.filters.applyFilters')}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
